// src/components/tutor/session_logs/SatPlanPrepCard.js
//
// Tutor Prepare-page plan overview (right column). READ-ONLY: tutors review
// the plan here but do not record progress (that happens in the Materials step).
// Subject-aware: fetches every plan for the student and renders ONLY the ones
// that cover the session's subject (blank subject = show all), so SAT, ACT,
// PSAT and custom plans all work. Renders nothing when no plan matches. Shows
// planned hours, target score, overall progress, the next recommended item, and
// a per-item progress bar with covered/planned hours. "View full plan" opens
// the student's Plan tab in a new browser tab.

import React, { useEffect, useMemo, useState } from "react";
import { IoSparklesSharp } from "react-icons/io5";
import { FiCheckCircle, FiCircle, FiChevronDown, FiChevronUp, FiExternalLink } from "react-icons/fi";
import {
  fetchPlansForStudentDir,
  summarizePlanProgress,
  itemProgressPct,
} from "../../../utils/sat/satPlanData";
import { planSubjectOrder, planTypeOf, PLAN_TYPES } from "../../../utils/plan/planConfig";
import { subjectFromSessionType, planMatchesSessionSubject, preferActivePlans } from "../../../utils/plan/planMatch";
import ProgressGauge from "../../sat/ProgressGauge";

const BAR_COLOR = { done: "#059669", in_progress: "#f59e0b", todo: "#cbd5e1" };

function statusOf(it) {
  const p = itemProgressPct(it);
  return p >= 100 ? "done" : p > 0 ? "in_progress" : "todo";
}

export default function SatPlanPrepCard({ studentDirId, sessionSubject, onResolved }) {
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const plans = await fetchPlansForStudentDir(studentDirId);
        if (alive) setAllPlans(plans || []);
      } catch {
        if (alive) setAllPlans([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [studentDirId]);

  // Every plan that covers the session's subject, active ones first. A student
  // can have several active plans as long as their subjects differ, so matching
  // plans are all shown; drafts that merely duplicate an active plan are not.
  const matching = useMemo(
    () =>
      preferActivePlans(
        (allPlans || [])
          .filter((p) => p.status !== "archived")
          .filter((p) => (p.checklist || []).some((i) => !i.isStretch))
          .filter((p) => planMatchesSessionSubject(p, sessionSubject))
      ).sort((a, b) => (a.status === "active" ? -1 : 0) - (b.status === "active" ? -1 : 0)),
    [allPlans, sessionSubject]
  );

  useEffect(() => {
    if (!loading && onResolved) onResolved(matching.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, matching.length]);

  if (loading || !matching.length) return null;
  return (
    <>
      {matching.map((plan) => (
        <PlanPrepCard
          key={plan.id}
          plan={plan}
          studentDirId={studentDirId}
          sessionSubject={sessionSubject}
        />
      ))}
    </>
  );
}

function PlanPrepCard({ plan, studentDirId, sessionSubject }) {
  const [showAll, setShowAll] = useState(false);
  const [showTopics, setShowTopics] = useState(false);
  const required = (plan.checklist || []).filter((i) => !i.isStretch);
  if (!required.length) return null;

  const type = planTypeOf(plan);
  const label = PLAN_TYPES[type].label;
  const isACT = type === "ACT";
  const order = planSubjectOrder(plan);
  const focusSubject = subjectFromSessionType(sessionSubject, order);
  const progress = summarizePlanProgress(plan);
  const subjectsPresent = order.filter((s) => required.some((i) => i.subject === s));
  // ACT shows the full plan (all sections) by default, highlighting the session's
  // section. SAT focuses on the session subject with a toggle to view both.
  const subjectsToShow =
    isACT || showAll || !focusSubject || !subjectsPresent.includes(focusSubject) ? subjectsPresent : [focusSubject];
  // Topics are collapsible — the header + overall progress always stay visible.
  const visibleSubjects = showTopics ? subjectsToShow : [];

  const openFullPlan = () => {
    const dir = plan.studentDirId || studentDirId;
    if (dir) {
      const q = plan.id ? `?plan=${encodeURIComponent(plan.id)}` : "";
      window.open(`/students/directory/${encodeURIComponent(dir)}/plan${q}`, "_blank", "noopener");
    }
  };

  return (
    <div className="slw-card">
      <div style={st.head}>
        <div style={st.headLeft}>
          <IoSparklesSharp size={14} />
          <strong>{plan.title || `${label} Plan`}</strong>
          <span style={st.pill}>{plan.totalHours}h</span>
          {plan.targetScore != null && <span style={st.pill}>Target {plan.targetScore}</span>}
        </div>
        <div style={st.headActions}>
          <span style={st.headProgress}>
            <ProgressGauge percent={progress.percent} size={34} stroke={4.5} showLabel={false} />
            <span style={st.headProgressText}>
              <strong style={st.headProgressPct}>{progress.percent}%</strong>
              {` ${progress.doneCount}/${progress.requiredCount} items · ${progress.coveredHours}/${progress.plannedHours}h`}
            </span>
          </span>
          <button
            type="button"
            style={st.topicsToggle}
            onClick={() => setShowTopics((v) => !v)}
            aria-expanded={showTopics}
          >
            {showTopics ? "Hide topics" : "Show topics"}
            {showTopics ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
          </button>
          <button type="button" style={st.fullLink} onClick={openFullPlan}>
            <FiExternalLink size={12} /> View full plan
          </button>
        </div>
      </div>

      {showTopics && !isACT && focusSubject && subjectsPresent.length > 1 && (
        <button type="button" style={st.toggle} onClick={() => setShowAll((v) => !v)}>
          {showAll ? `Focus on ${focusSubject}` : "View both subjects"}
        </button>
      )}

      {visibleSubjects.map((subject) => {
        const items = required.filter((i) => i.subject === subject);
        const next = items.find((i) => statusOf(i) !== "done");
        return (
          <div key={subject} style={st.subjectBlock}>
            <div style={st.subjectHead}>
              <span>{subject}</span>
              {focusSubject === subject && <span style={st.focusTag}>This session</span>}
            </div>
            {items.map((it) => {
              const pct = itemProgressPct(it);
              const status = statusOf(it);
              const done = status === "done";
              const isNext = next && it.id === next.id;
              const covered = Number(it.coveredHours) || 0;
              return (
                <div key={it.id} style={{ ...st.row, ...(isNext ? st.rowNext : null) }}>
                  <span style={{ marginTop: 1 }}>
                    {done ? <FiCheckCircle size={16} color="#16a34a" /> : <FiCircle size={16} color="#cbd5e1" />}
                  </span>
                  <div style={st.rowBody}>
                    <div style={st.rowTitleRow}>
                      <span
                        style={{
                          ...st.rowTitle,
                          textDecoration: done ? "line-through" : "none",
                          color: done ? "#94a3b8" : "#0f172a",
                        }}
                      >
                        <span style={st.rank}>{it.subjectRank}.</span> {it.subcategory}
                      </span>
                      <span style={st.rowHours}>
                        {covered > 0 ? `${covered}/${it.allocatedHours}` : it.allocatedHours}h
                      </span>
                    </div>
                    <div style={st.rowSub}>
                      {it.majorCategory}
                      {it.accuracyPct != null ? ` · ${it.accuracyPct}%` : ""}
                      {isNext && !done && <span style={st.nextTag}>next</span>}
                    </div>
                    <div style={st.miniBarOuter}>
                      <div style={{ ...st.miniBarInner, width: `${pct}%`, background: BAR_COLOR[status] }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

const st = {
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 0, flexWrap: "wrap" },
  headLeft: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  headActions: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  topicsToggle: { display: "inline-flex", alignItems: "center", gap: 3, border: "none", background: "transparent", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, fontFamily: "inherit" },
  pill: { fontSize: 11, fontWeight: 600, background: "#f1f5f9", color: "#475569", padding: "2px 8px", borderRadius: 999 },
  fullLink: { display: "inline-flex", alignItems: "center", gap: 4, border: "none", background: "transparent", color: "#1d4ed8", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 },
  headProgress: { display: "inline-flex", alignItems: "center", gap: 7 },
  headProgressText: { fontSize: 12, color: "#64748b", whiteSpace: "nowrap" },
  headProgressPct: { color: "#0f172a", fontWeight: 800 },
  toggle: { border: "none", background: "transparent", color: "#1d4ed8", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 8 },
  subjectBlock: { marginTop: 6 },
  subjectHead: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", color: "#334155", margin: "8px 0 4px" },
  focusTag: { fontSize: 10, fontWeight: 700, background: "#dbeafe", color: "#1e40af", padding: "1px 6px", borderRadius: 999, textTransform: "none", letterSpacing: 0 },
  row: { display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 0", borderTop: "1px solid #f1f5f9" },
  rowNext: { background: "#f8fafc" },
  rowBody: { flex: 1, minWidth: 0 },
  rowTitleRow: { display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" },
  rowTitle: { fontSize: 13, fontWeight: 600 },
  rank: { color: "#94a3b8", fontWeight: 700 },
  rowHours: { fontSize: 12, fontWeight: 700, color: "#0f172a", flexShrink: 0 },
  rowSub: { fontSize: 11.5, color: "#64748b", marginTop: 1, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" },
  nextTag: { fontSize: 10, fontWeight: 700, background: "#dcfce7", color: "#166534", padding: "1px 6px", borderRadius: 999 },
  miniBarOuter: { height: 8, background: "#e2e8f0", borderRadius: 999, overflow: "hidden", marginTop: 6 },
  miniBarInner: { height: "100%", borderRadius: 999 },
};
